/**
 * CompanyService — the current logged-in customer (platform mode).
 *
 * Same pattern as ViewerService: the auth guard fetches it once after sign-in
 * and it's exposed as a signal the top bar reads for the customer name + logo.
 * Uses the platform's `myCompany` query, which infers the customer from the
 * token (no argument) and returns the name + a directly-usable logo URL.
 *
 * Demo mode never fetches — name/logo fall back to the design placeholder.
 */

import { Injectable, inject, signal } from '@angular/core';

import { RDX_VISITORS_ENV } from './env';
import { GqlClient } from './gql-client';

/** The current customer, as the shell needs it. */
export interface Company {
  name: string | null;
  /** Logo URL (usable directly as an <img> src), or null. */
  logo: string | null;
  /** The brand colour (hex), the Division accent's source; null when unset. */
  primaryColor: string | null;
  /** Logos for light and dark papers, when the customer set them. */
  logoLight: string | null;
  logoDark: string | null;
}

/** `myCompany` = basic info for the current user's customer (no args). */
const MY_COMPANY_QUERY = `query MyCompany {
  myCompany {
    name
    logo
    logoLight
    logoDark
    primaryColor
  }
}`;

interface MyCompanyResponse {
  myCompany: {
    name?: string | null;
    logo?: string | null;
    logoLight?: string | null;
    logoDark?: string | null;
    primaryColor?: string | null;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private readonly gql = inject(GqlClient);

  private readonly _company = signal<Company | null>(null);
  /** The current customer (null in demo mode, or before the fetch resolves). */
  readonly company = this._company.asReadonly();

  private loaded = false;
  private inFlight: Promise<void> | null = null;

  /**
   * Fetch the current customer once. Single-flight + cached (the guard runs on
   * every navigation). No-op in demo mode. Failures are swallowed — the top bar
   * keeps the placeholder name + logo.
   */
  ensureLoaded(): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE || this.loaded) return Promise.resolve();
    this.inFlight ??= this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async load(): Promise<void> {
    try {
      const data = await this.gql.gql<MyCompanyResponse>(MY_COMPANY_QUERY);
      const c = data.myCompany;
      if (c) {
        this._company.set({
          name: c.name ?? null,
          logo: c.logo ?? null,
          logoLight: c.logoLight ?? null,
          logoDark: c.logoDark ?? null,
          primaryColor: c.primaryColor ?? null,
        });
        this.loaded = true;
      }
    } catch {
      // Best-effort — a failed customer fetch must not block the app; the top
      // bar falls back to the placeholder logo + name.
    }
  }
}
